import { useMemo } from 'react';
import { OnboardingFlowTestFlag } from '../OptimizelyFlagsConstants';
import { useAppSelector } from '../../store/hooks';
import {
  JoistSolutionOrgName,
  ServiceFusionSolutionOrgName,
  ServiceNationSolutionOrgName,
} from '../constants';
import {
  selectMemberSession,
  selectOptimizelyClientStatus,
} from '../../store/slices/onboardingSlice';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { normalizeOptimizelyEmail } from '../utils/optimizelyAttributes';

type OptimizelyClientStatus = 'pending' | 'success' | 'error';

interface OnboardingFlowTestDecisionParams {
  enabled?: boolean;
  memberEmail: string;
  memberId: string;
  optimizelyClientStatus: OptimizelyClientStatus;
  solutionOrgName: string;
}

export function isOnboardingFlowTestEligibleSolutionOrg(solutionOrgName?: string) {
  return !!solutionOrgName &&
    solutionOrgName !== JoistSolutionOrgName &&
    solutionOrgName !== ServiceFusionSolutionOrgName &&
    solutionOrgName !== ServiceNationSolutionOrgName;
}

export function getOnboardingFlowTestDecision({
  enabled = true,
  memberEmail,
  memberId,
  optimizelyClientStatus,
  solutionOrgName,
}: OnboardingFlowTestDecisionParams) {
  const isEligibleSolutionOrg = isOnboardingFlowTestEligibleSolutionOrg(solutionOrgName);

  if (!enabled ||
    !isEligibleSolutionOrg ||
    optimizelyClientStatus !== 'success' ||
    !global.optimizely ||
    !memberId) {
    return {
      isTestReady: !enabled || !isEligibleSolutionOrg || optimizelyClientStatus !== 'pending',
      shouldSkipOnboarding: false,
      variationKey: null,
    };
  }

  const decision = global.optimizely.decide(
    OnboardingFlowTestFlag,
    undefined,
    memberId,
    // Byte-exact in Optimizely's audiences - see normalizeOptimizelyEmail.
    { email: normalizeOptimizelyEmail(memberEmail), solutionOrgName },
  );

  return {
    isTestReady: true,
    shouldSkipOnboarding: decision.enabled,
    variationKey: decision.variationKey,
  };
}

export function useOnboardingFlowTest({ enabled = true } = {}) {
  const { memberEmail, memberId } = useAppSelector(selectMemberSession);
  const optimizelyClientStatus = useAppSelector(selectOptimizelyClientStatus);
  const { solutionOrgName } = useSolutionOrg();

  return useMemo(
    () => getOnboardingFlowTestDecision({
      enabled,
      memberEmail,
      memberId,
      optimizelyClientStatus,
      solutionOrgName: solutionOrgName as string,
    }),
    [enabled, memberEmail, memberId, optimizelyClientStatus, solutionOrgName],
  );
}
